import { Box, Grid } from "@mui/material";
import {
  IBandwidthFailuresResponseModel,
  IStatisticsUptimeResponseModel,
} from "../system-performance/models/chart";
import React, { useMemo } from "react";

import { AutocompleteOptionType } from "../../../../common/models/autocomplete";
import ChartTableContainer from "./components/ChartTableContainer";
import { eventType } from "../../../../API";
import { useCreateSystemHealthChart } from "./hooks/useCreateSystemHealthChart";
import { v4 as uuidv4 } from "uuid";
import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";

type SystemHealthTabProps = {
  selectedLocation: AutocompleteOptionType[] | null;
};

const SystemHealthTab: React.FC<SystemHealthTabProps> = ({
  selectedLocation,
}) => {
  const locationMapped = selectedLocation
    ? selectedLocation?.map(l => l?.value ?? "")
    : [];

  const timeframeDays = useMemo(() => 56, []);
  const bandwidthFailuresTimeframeDays = useMemo(() => 7, []);

  const customerId = useCustomerIdGuard();

  const jobDependencies = [selectedLocation, customerId];

  const nodeUptimeJobId = useMemo(() => uuidv4(), jobDependencies);
  const deviceUptimeJobId = useMemo(() => uuidv4(), jobDependencies);
  const serviceUptimeJobId = useMemo(() => uuidv4(), jobDependencies);
  const bandwidthFailuresJobId = useMemo(() => uuidv4(), jobDependencies);

  const getNodeUptimeChart =
    useCreateSystemHealthChart<IStatisticsUptimeResponseModel>(
      timeframeDays,
      locationMapped,
      eventType.nodeUptime,
      nodeUptimeJobId
    );

  const getDeviceUptimeChart =
    useCreateSystemHealthChart<IStatisticsUptimeResponseModel>(
      timeframeDays,
      locationMapped,
      eventType.deviceUptime,
      deviceUptimeJobId
    );

  const getServiceUptimeChart =
    useCreateSystemHealthChart<IStatisticsUptimeResponseModel>(
      timeframeDays,
      locationMapped,
      eventType.serviceUptime,
      serviceUptimeJobId
    );

  const getBandwidthFailuresChart =
    useCreateSystemHealthChart<IBandwidthFailuresResponseModel>(
      bandwidthFailuresTimeframeDays,
      locationMapped,
      eventType.bandwidthFailures,
      bandwidthFailuresJobId
    );

  const tooltipTexts = useMemo(
    () => ({
      nodeUptime:
        "Node Uptime: the percentage of expected heartbeats successfully received from a node. Calculated as: (Heartbeats received / total expected) x 100 over a weekly period. Each node is expected to check in every 5 minutes.",
      deviceUptime:
        "Device Uptime: shows how often this specific device successfully reported in. It's based on the number of heartbeats received compared to what was expected over the week.",
      serviceUptime:
        "Service Uptime: shows what percentage of 15-minute time windows each service type (fire, smoke, etc.) was actively reporting its status during the week. It compares how many time windows received heartbeats versus the total expected windows for the week.",
      uploadFailures:
        "Upload Failures: shows how often a node failed to upload data despite checking in. Calculated as: (Failed uploads / heartbeats received) x 100. A high percentage may indicate network or connectivity issues.",
    }),
    []
  );

  return (
    <Box>
      <Grid container alignItems="center" justifyContent="center" spacing={3}>
        <Grid item xs={12} sm={12}>
          <ChartTableContainer
            chartsData={getNodeUptimeChart}
            title="Node uptime (%)"
            labelText="Showing data for up to 56 days — full weeks only"
            tooltipText={tooltipTexts.nodeUptime}
            tableItemName="Node"
            type="line"
            jobId={nodeUptimeJobId}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <ChartTableContainer
            chartsData={getDeviceUptimeChart}
            title="Device uptime (%)"
            labelText="Showing data for up to 56 days — full weeks only"
            tooltipText={tooltipTexts.deviceUptime}
            tableItemName="Device"
            type="line"
            jobId={deviceUptimeJobId}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <ChartTableContainer
            chartsData={getServiceUptimeChart}
            title="Service uptime (%)"
            labelText="Showing data for up to 56 days — full weeks only"
            tooltipText={tooltipTexts.serviceUptime}
            tableItemName="Service"
            type="line"
            jobId={serviceUptimeJobId}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <ChartTableContainer
            chartsData={getBandwidthFailuresChart}
            labelText="Showing data for up to 7 days"
            title="Upload Failures (%)"
            tooltipText={tooltipTexts.uploadFailures}
            tableItemName="Node"
            type="line"
            jobId={bandwidthFailuresJobId}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemHealthTab;
